import { useCallback, useEffect } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useSearchParams } from "react-router";
import { cn } from "@/lib/utils";

const WIDTH = { md: "sm:w-[480px]", lg: "sm:w-[640px]" } as const;
export type SidePanelSize = keyof typeof WIDTH;

interface SidePanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Avatar/ícone à esquerda do título (ex.: PersonAvatar do candidato). */
  leading?: React.ReactNode;
  /** Ações no cabeçalho, antes do fechar. */
  headerActions?: React.ReactNode;
  /** Abas ou filtros logo abaixo do cabeçalho. */
  tabs?: React.ReactNode;
  footer?: React.ReactNode;
  size?: SidePanelSize;
  /** Troca o padding do corpo (ex.: "p-0" quando o conteúdo tem rolagem própria). */
  bodyClassName?: string;
  children: React.ReactNode;
}

/**
 * Padrão C do plano de UI: painel à direita que NÃO trava a lista. Sem
 * escurecer o fundo e sem fechar ao clicar fora: clicar em outro item da
 * lista só troca o conteúdo. Fecha no X ou Esc. Em telas largas o conteúdo
 * da página ganha padding à direita para não ficar embaixo do painel.
 * Nunca abra dialog de dentro dele; renderize como irmão.
 */
export function SidePanel({
  open,
  onOpenChange,
  title,
  subtitle,
  leading,
  headerActions,
  tabs,
  footer,
  size = "md",
  bodyClassName,
  children,
}: SidePanelProps) {
  useEffect(() => {
    if (!open) return;
    document.body.dataset.sidePanel = size;
    return () => {
      delete document.body.dataset.sidePanel;
    };
  }, [open, size]);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          onInteractOutside={(event) => event.preventDefault()}
          onOpenAutoFocus={(event) => event.preventDefault()}
          className={cn(
            "fixed bottom-0 right-0 top-14 z-40 flex w-full flex-col border-l bg-card shadow-xl outline-hidden",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right data-[state=open]:duration-200",
            WIDTH[size],
          )}
        >
          <div className="flex items-start gap-3 border-b px-4 py-3">
            {leading && <div className="shrink-0">{leading}</div>}
            <div className="min-w-0 flex-1">
              <DialogPrimitive.Title className="truncate text-sm font-semibold">{title}</DialogPrimitive.Title>
              {subtitle ? (
                <DialogPrimitive.Description className="truncate text-xs text-muted-foreground">{subtitle}</DialogPrimitive.Description>
              ) : (
                <DialogPrimitive.Description className="sr-only">Painel lateral</DialogPrimitive.Description>
              )}
            </div>
            {headerActions}
            <DialogPrimitive.Close
              className="-mr-1 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Fechar painel"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          {tabs && <div className="border-b px-3">{tabs}</div>}
          <div className={cn("min-h-0 flex-1 overflow-y-auto px-4 py-4", bodyClassName)}>{children}</div>
          {footer && <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-4 py-3">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

/**
 * Estado do painel na URL: ?painel=negociacao&item=123. Link compartilhável,
 * e o Voltar do navegador fecha o painel.
 */
export function useSidePanel<Tab extends string = string>(name: string) {
  const [params, setParams] = useSearchParams();
  const isOpen = params.get("painel")?.split(":")[0] === name;
  const itemId = isOpen ? params.get("item") : null;
  const tab = (isOpen ? params.get("painel")?.split(":")[1] ?? null : null) as Tab | null;

  const open = useCallback(
    (item?: string | number | null, openTab?: Tab) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("painel", openTab ? `${name}:${openTab}` : name);
          if (item != null) next.set("item", String(item));
          else next.delete("item");
          return next;
        },
        { replace: isOpen },
      );
    },
    [name, setParams, isOpen],
  );

  const setTab = useCallback(
    (nextTab: Tab) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          next.set("painel", `${name}:${nextTab}`);
          return next;
        },
        { replace: true },
      );
    },
    [name, setParams],
  );

  const close = useCallback(() => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("painel");
        next.delete("item");
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  return { isOpen, itemId, tab, open, setTab, close };
}
